import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
    UserIcon,
    MapPinIcon,
    ClockIcon,
    ArrowLeftIcon,
} from "@heroicons/react/24/solid";
import Layout from "../../../components/Layout";
import apiClient from "../../../services/apiClient";
import { ApiResponse, UserLocation } from "../UserLocations";
import DocumentModal from "../../../hooks/DocumentPreview";

export default function PetOwnerPage() {
    const location = useLocation();
    const navigate = useNavigate();
    const { item } = location.state || {};
    console.log('item', JSON.stringify(item, null, 2));
    const [owners, setOwners] = useState<any>();
    const [locations, setLocations] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [modalSrc, setModalSrc] = useState<string | null>(null);

    const openDocumentModal = (src: string) => {
        if (!src) return;
        setModalSrc(src);
    };
    const getFullUrl = (url?: string): string | undefined => {
        const baseUrl = "https://a2zschoolfolio.s3.ap-south-1.amazonaws.com/";
        if (!url) return undefined; // ✅ return undefined instead of null
        return url.startsWith("http") ? url : `${baseUrl}${url}`;
    };
    useEffect(() => {
        if (!item?.id) return;

        const controller = new AbortController();
        const fetchData = async () => {
            try {
                setLoading(true);
                const [ownerRes, locationRes] = (await Promise.all([
                    apiClient(`v1/resource/patients/${item.id}`),
                    apiClient(`v1/resource/UserLocation?user_id=${item.user_id}&is_confirmed=1`),
                ])) as any[];

                setOwners(ownerRes?.data?.patients || null);
                setLocations(locationRes?.data?.user_locations || []);
            } catch (err: any) {
                if (err.name !== "AbortError") console.error("❌ Fetch failed:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
        return () => controller.abort();
    }, [item?.id, item?.user_id]);

    const handleGoBack = () => navigate(-1);

    const user = owners?.user || {};
    const address = user?.address || {};
    const timezone = user?.timezone || {};

    // 🧱 Reusable row
    const InfoRow = ({ label, value }: { label: string; value?: React.ReactNode }) =>
        value ? (
            <div className="flex justify-between border-b border-gray-200 py-3 px-2 hover:bg-indigo-50 rounded transition-colors">
                <span className="text-gray-600 font-medium">{label}</span>
                <span className="text-gray-900 truncate max-w-xs">
                    {value || <span className="italic text-gray-400">N/A</span>}
                </span>
            </div>
        ) : null;

    const BooleanBadge = ({ value }: { value: boolean }) => (
        <span
            className={`inline-block px-3 py-0.5 text-sm font-semibold rounded-full ${value ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                }`}
        >
            {value ? "Yes" : "No"}
        </span>
    );

    // 🗺️ Extract confirmed address
    const confirmedAddress =
        locations[0]?.additional_info?.address_details || {};
    console.log('locations', locations[0]?.additional_info?.display_address)
    const displayAddress = locations[0]?.additional_info?.display_address;

    if (!item) {
        return (
            <Layout>
                <div className="min-h-screen flex items-center justify-center bg-gray-50">
                    <p className="text-gray-500 text-lg italic">
                        No pet owner details available.
                    </p>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-white py-8 px-4 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto">
                    {/* 🔙 Header */}
                    <div className="flex items-center justify-between mb-12 border-b border-indigo-300 pb-5">
                        <div className="flex items-center space-x-2">
                            <button
                                onClick={handleGoBack}
                                className="flex items-center text-indigo-900 hover:text-indigo-700 transition"
                                aria-label="Go back"
                            >
                                <ArrowLeftIcon className="h-6 w-6" />
                            </button>
                            <h1 className="text-4xl font-extrabold text-indigo-900">
                                Pet Owner Details
                            </h1>
                        </div>
                    </div>

                    {/* 💠 Grid */}
                    <div className="grid gap-12 md:grid-cols-2">
                        {/* Owner Info */}
                        {owners && (
                            <section className="bg-white rounded-2xl shadow-xl p-10 ring-1 ring-indigo-100 hover:shadow-2xl transition-shadow duration-300">
                                <header className="flex items-center mb-8 space-x-4">
                                    <UserIcon className="text-indigo-600 w-8 h-8" />
                                    <h2 className="text-3xl font-semibold text-indigo-900 tracking-wide">
                                        Owner Information
                                    </h2>
                                </header>

                                {owners?.additional_info?.avatar_url?.file_path && (
                                    <div className="flex justify-center mb-6">
                                        <img
                                            src={owners.additional_info.avatar_url.file_path}
                                            alt="Profile"
                                            className="w-32 h-32 object-cover border-4 border-indigo-200 shadow-md rounded-lg"
                                        />
                                    </div>
                                )}

                                <InfoRow
                                    label="Name"
                                    value={`${user?.first_name ?? ""} ${user?.last_name ?? ""}`.trim()}
                                />
                                <InfoRow label="Email" value={user?.email} />
                                <InfoRow
                                    label="Mobile"
                                    value={`${user?.isd_code ?? ""} ${user?.mobile ?? ""}`.trim()}
                                />
                                <InfoRow label="Username" value={user?.username} />
                                <InfoRow label="Date of Birth" value={user?.dob} />
                                <InfoRow label="Gender" value={user?.gender} />
                                {user?.is_2fa !== undefined && (
                                    <InfoRow
                                        label="2FA Enabled"
                                        value={<BooleanBadge value={user.is_2fa} />}
                                    />
                                )}
                                {user?.is_active !== undefined && (
                                    <InfoRow
                                        label="Active"
                                        value={<BooleanBadge value={user.is_active} />}
                                    />
                                )}

                                <header className="flex items-center mt-12 mb-6 space-x-4">
                                    <MapPinIcon className="text-indigo-600 w-8 h-8" />
                                    <h2 className="text-3xl font-semibold text-indigo-900 tracking-wide">
                                        Address
                                    </h2>
                                </header>

                                {displayAddress ? (
                                    <div className="px-2 py-3">
                                        <p className="text-gray-700">{displayAddress}</p>
                                    </div>
                                ) : (
                                    <p className="text-gray-500 italic px-2 py-3">No address available.</p>
                                )}

                            </section>
                        )}

                        {timezone && (
                            <section className="bg-white rounded-2xl shadow-xl p-10 ring-1 ring-indigo-100 hover:shadow-2xl transition-shadow duration-300">
                                <header className="flex items-center mb-6 space-x-4">
                                    <ClockIcon className="text-indigo-600 w-8 h-8" />
                                    <h2 className="text-3xl font-semibold text-indigo-900 tracking-wide">
                                        Timezone
                                    </h2>
                                </header>
                                <InfoRow label="Zone" value={timezone?.zone_name} />
                                <InfoRow label="Country Code" value={timezone?.country_code} />
                                {(owners?.additional_info?.avatar_url?.trim() || user?.profile_image?.trim()) && (
                                    <InfoRow
                                        label="Avatar"
                                        value={
                                            <img
                                                src={getFullUrl(owners?.additional_info?.avatar_url) || getFullUrl(user?.profile_image)}
                                                alt="Avatar"
                                                className="h-32 w-32 object-cover rounded-lg shadow-md cursor-pointer hover:opacity-80 transition"
                                                onClick={() =>
                                                    openDocumentModal(
                                                        getFullUrl(owners?.additional_info?.avatar_url) || getFullUrl(user?.profile_image) || ""
                                                    )
                                                }

                                            />
                                        }
                                    />
                                )}

                            </section>
                        )}

                    </div>
                </div>
            </div>
            {modalSrc && (
                <DocumentModal
                    src={modalSrc}
                    onClose={() => setModalSrc(null)}
                />
            )}

        </Layout>
    );
}
